import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listProfiles, createUser } from "../services/user";
import { listTeachers } from "../services/teacher";
import { listWorkers } from "../services/worker";
import { getUserInfo, getSucursalInfo } from "../services/auth";
import WizardSteps from "./shared/WizardSteps";
import MultiSelectField from "./shared/MultiSelectField";

const TOTAL_STEPS = 2;
const PROFESSOR_DESCRIPTION = "Professor(a)";

// Ported from sge_infantil_web/src/pages/user/create/index.js. This screen
// does NOT collect free-form personal data — it converts an existing
// Teacher or Worker record into a system login by picking a Profile
// (role) and then the Teacher/Worker to attach; name/email/contact are
// pulled from that record and locked. The original also supports arriving
// here with a stashed `localStorage.getItem('user')` record (from a
// "Novo Utilizador" action elsewhere) — that pre-population path is an
// edge case from a screen not built yet in this port, intentionally
// skipped here.
const initialState = {
  profileId: "",
  selectedTeacher: "",
  selectedWorker: "",
  name: "",
  email: "",
  contact: "",
  sucursais: [],
};

const buildUsername = (fullName) => {
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/);
  const lastName = parts[parts.length - 1];
  const firstLetter = trimmed.substring(0, 1);
  return `${firstLetter}${lastName}`.toLowerCase() + new Date().getFullYear();
};

const CadastrarUtilizadorLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [profiles, setProfiles] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [username, setUsername] = useState("");

  useEffect(() => {
    listProfiles()
      .then((resp) => setProfiles(resp.data || []))
      .catch(() => setProfiles([]));
    listTeachers()
      .then((resp) => setTeachers(resp.data || []))
      .catch(() => setTeachers([]));
    listWorkers()
      .then((resp) => setWorkers(resp.data || []))
      .catch(() => setWorkers([]));
  }, []);

  const selectedProfile = profiles.find((p) => String(p.id) === String(form.profileId));
  const isProfessor = selectedProfile?.description === PROFESSOR_DESCRIPTION;
  const fieldsLocked = Boolean(form.selectedTeacher) || Boolean(form.selectedWorker);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const stepLabels = [
    t("utilizador.wizard.step.profile"),
    t("utilizador.wizard.step.confirm"),
  ];

  const handleSelectProfile = (e) => {
    const profileId = e.target.value;
    setForm((f) => ({
      ...f,
      profileId,
      // Switching profile invalidates whichever teacher/worker was chosen
      // under the previous profile type, so the linked fields are cleared.
      selectedTeacher: "",
      selectedWorker: "",
      name: "",
      email: "",
      contact: "",
    }));
  };

  const handleSelectTeacher = (e) => {
    const teacherId = e.target.value;
    const teacher = teachers.find((t2) => String(t2.id) === String(teacherId));
    setForm((f) => ({
      ...f,
      selectedTeacher: teacherId,
      name: teacher?.name || "",
      email: teacher?.email || "",
      contact: teacher?.contact || "",
    }));
  };

  const handleSelectWorker = (e) => {
    const workerId = e.target.value;
    const worker = workers.find((w) => String(w.id) === String(workerId));
    setForm((f) => ({
      ...f,
      selectedWorker: workerId,
      name: worker?.name || "",
      email: worker?.email || "",
      contact: worker?.contact || "",
    }));
  };

  const handleSucursaisChange = (values) => {
    setForm((f) => ({ ...f, sucursais: values.map(String) }));
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!form.profileId) {
        toast.error(t("utilizador.validation.profile.required"));
        return;
      }
      if (isProfessor && !form.selectedTeacher) {
        toast.error(t("utilizador.validation.teacher.required"));
        return;
      }
      if (!isProfessor && !form.selectedWorker) {
        toast.error(t("utilizador.validation.worker.required"));
        return;
      }
      if (!form.name || !form.contact) {
        toast.error(t("validation.required"));
        return;
      }
      if (form.contact.length !== 9) {
        toast.error(t("utilizador.validation.contact.length"));
        return;
      }
      if (!form.sucursais.length) {
        toast.error(t("utilizador.validation.sucursal.required"));
        return;
      }
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
    setUsername("");
  };

  const submit = async () => {
    setSubmitting(true);
    const userInfo = getUserInfo();
    const userId = userInfo?.id;
    const generatedUsername = buildUsername(form.name);
    const teacher = teachers.find((t2) => String(t2.id) === String(form.selectedTeacher));
    const worker = workers.find((w) => String(w.id) === String(form.selectedWorker));
    const availableSucursals = Array.isArray(userInfo?.sucursals) ? userInfo.sucursals : [];
    const resolvedSucursals = form.sucursais
      .map((id) => availableSucursals.find((s) => String(s.id) === String(id)))
      .filter(Boolean);

    const payload = {
      sucursalId: getSucursalInfo()?.id,
      name: form.name,
      email: form.email,
      contact: form.contact,
      address: "",
      profile: selectedProfile,
      username: generatedUsername,
      sucursals: resolvedSucursals,
      password: `#${generatedUsername}`,
      activatedBy: userId,
      createdBy: userId,
      ...(teacher ? { teacher: { name: teacher.name, id: teacher.id }, teacherId: teacher.id } : {}),
      ...(worker ? { worker: { name: worker.name, id: worker.id }, workerId: worker.id } : {}),
    };

    try {
      await createUser(payload);
      setUsername(generatedUsername);
      setDone(true);
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("utilizador.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("utilizador.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("utilizador.field.profile")} <span className='required-asterisk'>*</span></label>
                      <select
                        className='form-control form-select'
                        value={form.profileId}
                        onChange={handleSelectProfile}
                      >
                        <option value=''>{t("utilizador.select.placeholder")}</option>
                        {profiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isProfessor && (
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("utilizador.field.teacher")} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.selectedTeacher}
                          onChange={handleSelectTeacher}
                        >
                          <option value=''>{t("utilizador.select.placeholder")}</option>
                          {teachers.map((teacher) => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {!isProfessor && form.profileId && (
                      <div className='col-sm-6'>
                        <label className='form-label'>{t("utilizador.field.worker")} <span className='required-asterisk'>*</span></label>
                        <select
                          className='form-control form-select'
                          value={form.selectedWorker}
                          onChange={handleSelectWorker}
                        >
                          <option value=''>{t("utilizador.select.placeholder")}</option>
                          {workers.map((worker) => (
                            <option key={worker.id} value={worker.id}>
                              {worker.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className='col-sm-6'>
                      <label className='form-label'>{t("utilizador.field.name")} <span className='required-asterisk'>*</span></label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="f7:person" />
                        </span>
                        <input
                          type='text'
                          className='form-control'
                          value={form.name}
                          disabled={fieldsLocked}
                          onChange={setField("name")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("utilizador.field.email")} <span className='required-asterisk'>*</span></label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mage:email" />
                        </span>
                        <input
                          type='email'
                          className='form-control'
                          value={form.email}
                          disabled={fieldsLocked}
                          onChange={setField("email")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("utilizador.field.contact")} <span className='required-asterisk'>*</span></label>
                      <div className='input-group'>
                        <span className='input-group-text'>+258</span>
                        <input
                          type='text'
                          maxLength={9}
                          className='form-control'
                          value={form.contact}
                          disabled={fieldsLocked}
                          onChange={setField("contact")}
                        />
                      </div>
                    </div>
                    <div className='col-sm-12'>
                      <label className='form-label'>{t("utilizador.field.sucursais")} <span className='required-asterisk'>*</span></label>
                      <MultiSelectField
                        items={Array.isArray(getUserInfo()?.sucursals) ? getUserInfo().sucursals : []}
                        getOptionValue={(s) => String(s.id)}
                        getOptionLabel={(s) => s.description || s.name}
                        selectedIds={form.sucursais}
                        onChange={handleSucursaisChange}
                        placeholder={t("utilizador.select.placeholder")}
                        noOptionsMessage='-'
                      />
                      <span className='text-secondary-light text-sm'>
                        {t("utilizador.field.sucursais.hint")}
                      </span>
                    </div>
                  </div>
                  <div className='form-group text-end mt-24'>
                    <button
                      onClick={nextStep}
                      type='button'
                      className='form-wizard-next-btn btn btn-primary-600 px-32'
                    >
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("utilizador.confirm.description")}</div>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("utilizador.personal.info")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        <li>
                          <strong>{t("utilizador.field.name")}:</strong> {form.name}
                        </li>
                        <li>
                          <strong>{t("utilizador.field.email")}:</strong> {form.email}
                        </li>
                        <li>
                          <strong>{t("utilizador.field.contact")}:</strong> +258 {form.contact}
                        </li>
                        <li>
                          <strong>{t("utilizador.field.profile")}:</strong>{" "}
                          {selectedProfile?.description}
                        </li>
                        {isProfessor && (
                          <li>
                            <strong>{t("utilizador.field.teacher")}:</strong>{" "}
                            {teachers.find((t2) => String(t2.id) === String(form.selectedTeacher))?.name}
                          </li>
                        )}
                        {!isProfessor && (
                          <li>
                            <strong>{t("utilizador.field.worker")}:</strong>{" "}
                            {workers.find((w) => String(w.id) === String(form.selectedWorker))?.name}
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className='col-sm-6'>
                      <h6 className='text-md text-neutral-500 mb-16'>
                        {t("utilizador.field.sucursais")}
                      </h6>
                      <ul className='list-unstyled d-flex flex-column gap-8'>
                        {form.sucursais.map((id) => {
                          const sucursal = (getUserInfo()?.sucursals || []).find(
                            (s) => String(s.id) === String(id),
                          );
                          return <li key={id}>{sucursal?.description || sucursal?.name}</li>;
                        })}
                      </ul>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      onClick={prevStep}
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button
                      onClick={submit}
                      type='button'
                      className='form-wizard-submit btn btn-primary-600 px-32'
                      disabled={submitting}
                    >
                      {submitting ? (
                        "..."
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>
                      {t("utilizador.create.success.title")}
                    </h6>
                    <p className='text-neutral-400 text-sm mb-0'>
                      {t("utilizador.create.success.subtitle")}: <strong>{username}</strong>
                    </p>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button
                      type='button'
                      className='btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={restart}
                    >
                      {t("utilizador.create.new")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/utilizadores/lista")}
                    >
                      <i className='ri-arrow-left-line' /> {t("utilizador.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarUtilizadorLayer;
